// components/DashboardAnalytics.jsx - UPDATED VERSION
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  CircularProgress,
  Chip,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Paper,
  CardContent,
  Divider,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  People,
  CheckCircle,
  Schedule,
  Whatshot,
  Business,
  Refresh,
  Close,
  Phone,
  CalendarToday,
  Payment,
  Description,
  Notifications,
  Warning,
  Groups,
  AdminPanelSettings,
  Dashboard as DashboardIcon,
  AccountBalanceWallet,
  PendingActions,
  EventBusy,
  Receipt,
  AttachMoney,
  Person,
  BarChart,
  AccessTime,
  Place,
  OpenInNew,
  Pending,
  DateRange,
  Info,
  ChevronRight
} from '@mui/icons-material';
import { url } from '../constants/url';
import { useThemeToggle } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  getDashboardAnalytics,
  getTodaysOverview,
  getTomorrowsReminders,
  getTeamPerformanceDetails,
  getCompanyPerformance,
  getTeamMemberQuotationAnalytics,
  getTeamPerformanceWithQuotations,
  getTeamMemberPersonalQuotationAnalytics,
  getTeamMemberDetailedAnalytics
} from './dashboardService';


// ==================== CONSTANTS ====================
const months = [
  { value: '01', label: 'January' }, { value: '02', label: 'February' },
  { value: '03', label: 'March' }, { value: '04', label: 'April' },
  { value: '05', label: 'May' }, { value: '06', label: 'June' },
  { value: '07', label: 'July' }, { value: '08', label: 'August' },
  { value: '09', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' }
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => (2020 + i).toString()).reverse();

const COLORS_BASE = {
  primary: '#1E8E3E',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  purple: '#8B5CF6',
  indigo: '#4F46E5',
  cyan: '#06B6D4',
};

const getColors = (mode) => ({
  ...COLORS_BASE,
  primary: mode === 'light' ? '#1E8E3E' : '#34D399',
});

const getGradients = (mode) => ({
  primary: mode === 'light'
    ? 'linear-gradient(135deg, #1E8E3E 0%, #34A853 100%)'
    : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
  success: mode === 'light'
    ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
    : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
  warning: mode === 'light'
    ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
    : 'linear-gradient(135deg, #FBBF24 0%, #D97706 100%)',
  error: mode === 'light'
    ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
    : 'linear-gradient(135deg, #F87171 0%, #DC2626 100%)',
  info: mode === 'light'
    ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
    : 'linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)',
  purple: mode === 'light'
    ? 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
    : 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)',
  indigo: mode === 'light'
    ? 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)'
    : 'linear-gradient(135deg, #818CF8 0%, #3730A3 100%)',
  cyan: mode === 'light'
    ? 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)'
    : 'linear-gradient(135deg, #22D3EE 0%, #0891B2 100%)',
});

// ==================== STAT CARD COMPONENT ====================
const StatCard = ({ title, value, subtitle, icon, gradient, onClick, isMobile }) => (
  <Card
    sx={{
      p: 2,
      borderRadius: 2,
      background: gradient,
      color: 'white',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.3s ease',
      flex: 1,
      minWidth: isMobile ? '100%' : '180px',
      maxWidth: '220px',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
      } : {},
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: 'rgba(255,255,255,0.3)',
      }
    }}
    onClick={onClick}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, color: 'white', fontSize: '1.5rem' }}>
          {value}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white', mb: 0.5, fontSize: '0.9rem' }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'white', opacity: 0.9, fontSize: '0.7rem' }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{
        p: 1,
        borderRadius: 1.5,
        backgroundColor: 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 20, color: 'white' } })}
      </Box>
    </Box>
  </Card>
);

// ==================== COMPACT QUOTATION CARD COMPONENT - UPDATED ====================
const CompactQuotationCard = ({ title, amount, icon, gradient, description }) => (
  <Card
    sx={{
      p: 2,
      borderRadius: 2,
      background: gradient,
      color: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      position: 'relative',
      overflow: 'hidden',
      minWidth: '160px',
      maxWidth: '200px',
      flex: '1 1 160px',
      height: '120px',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'rgba(255,255,255,0.3)',
      }
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box sx={{ flex: 1 }}>
        {/* Amount with proper spacing below */}
        <Typography variant="h5" sx={{
          fontWeight: 800,
          mb: 5, // Added spacing below amount
          color: 'white',
          fontSize: '1.2rem',
          lineHeight: 1.2,
          mt: 1
        }}>
          ₹{(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </Typography>

        {/* Title with proper spacing below */}
        <Typography variant="body2" sx={{
          fontWeight: 600,
          color: 'white',
          fontSize: '0.8rem',
          lineHeight: 1.2,
          mb: 0.5 // Added spacing below title
        }}>
          {title}
        </Typography>

        {/* Subtitle/Description */}
        <Typography variant="caption" sx={{
          color: 'rgba(255,255,255,0.8)',
          fontSize: '0.65rem',
          lineHeight: 1.2
        }}>
          {description}
        </Typography>
      </Box>
      <Box sx={{
        p: 1,
        borderRadius: 1,
        backgroundColor: 'rgba(255,255,255,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 16, color: 'white' } })}
      </Box>
    </Box>
  </Card>
);

// ==================== TAB PANEL COMPONENT ====================
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`team-member-tabpanel-${index}`}
    aria-labelledby={`team-member-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

// ==================== TOMORROW'S REMINDERS COMPONENT - COMPACT VERSION ====================
const TomorrowsRemindersCard = ({
  remindersData,
  mode,
  COLORS,
  GRADIENTS,
  theme,
  alpha,
  navigate,
}) => {
  if (!remindersData) return null;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowFormatted = tomorrow.toISOString().split('T')[0];

  const cards = [
    {
      title: "Rescheduled",
      value: remindersData.rescheduled || 0,
      subtitle: "Calls for tomorrow",
      icon: <Schedule />,
      color: COLORS.warning,
      gradient: GRADIENTS.warning,
      onClick: () => navigate('/leads?filter=rescheduled&date=' + tomorrowFormatted),
      show: true,
      dateFilter: tomorrowFormatted
    },
    {
      title: "Meetings",
      value: remindersData.meetings || 0,
      subtitle: "Scheduled for tomorrow",
      icon: <CalendarToday />,
      color: COLORS.info,
      gradient: GRADIENTS.info,
      onClick: () => navigate('/leads?filter=meetings&date=' + tomorrowFormatted),
      show: true,
      dateFilter: tomorrowFormatted
    },
  ].filter(card => card.show);

  const hasNoReminders = cards.every(card => card.value === 0);

  return (
    <Card sx={{
      p: 2,
      mb: 2,
      borderRadius: 2,
      background: mode === 'light'
        ? 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
        : 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          p: 1,
          borderRadius: 1.5,
          background: mode === 'light' ? alpha(COLORS.warning, 0.1) : alpha(COLORS.warning, 0.2),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${alpha(COLORS.warning, 0.2)}`
        }}>
          <Notifications sx={{ color: COLORS.warning, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
            Tomorrow's Reminders
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {tomorrow.toLocaleDateString('en-IN', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
        </Box>
      </Box>

      {hasNoReminders ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            background: mode === 'light'
              ? `linear-gradient(135deg, ${alpha(COLORS.info, 0.05)} 0%, ${alpha(COLORS.info, 0.02)} 100%)`
              : `linear-gradient(135deg, ${alpha(COLORS.info, 0.1)} 0%, ${alpha(COLORS.info, 0.05)} 100%)`,
            borderRadius: 1.5,
            border: `1px dashed ${alpha(COLORS.info, 0.3)}`
          }}
        >
          <CheckCircle sx={{ fontSize: 32, color: alpha(COLORS.success, 0.5), mb: 1 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 0.5, fontSize: '0.9rem' }}>
            No Reminders for Tomorrow
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            You're all caught up! No scheduled calls, meetings, or payments due tomorrow.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 1.5,
            flexWrap: 'wrap',
            '& > *': {
              flex: '0 0 auto',
              maxWidth: '200px',
              minWidth: '180px'
            }
          }}
        >
          {cards.map((card, index) => (
            <Card
              key={index}
              sx={{
                p: 2,
                borderRadius: 2,
                background: card.gradient,
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                height: '100px',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 25px ${alpha(card.color, 0.3)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'rgba(255,255,255,0.3)',
                }
              }}
              onClick={card.onClick}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {React.cloneElement(card.icon, { sx: { fontSize: 18, color: 'white' } })}
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'white', fontSize: '1.2rem' }}>
                    {card.value}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'white', fontSize: '0.9rem' }}>
                    {card.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.7rem' }}>
                    {card.subtitle}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}
    </Card>
  );
};
const TodaysOverviewCard = ({
  overviewData,
  quotationFilters,
  mode,
  COLORS,
  GRADIENTS,
  theme,
  alpha,
  navigate,
}) => {
  if (!overviewData) return null;

  const cards = [
    {
      title: "Calls Assigned",
      value: overviewData.totalCallsAssigned || 0,
      subtitle: "Scheduled for today",
      icon: <Phone />,
      color: COLORS.primary,
      gradient: GRADIENTS.primary,
      onClick: () => {
        const params = new URLSearchParams();
        params.set('dateFilter', 'today');
        navigate(`/leads?${params.toString()}`);
      },
      show: true,
    },
    {
      title: "Calls Completed",
      value: overviewData.completedCalls || 0,
      subtitle: (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {`${overviewData.totalCallsAssigned > 0 ? Math.round((overviewData.completedCalls / overviewData.totalCallsAssigned) * 100) : 0}% completion`}
          <Tooltip title="Recorded every time you update a lead's status (e.g. to 'Received')" arrow placement="top">
            <Info sx={{ fontSize: 14, opacity: 0.7, cursor: 'help' }} />
          </Tooltip>
        </Box>
      ),
      icon: <CheckCircle />,
      color: COLORS.success,
      gradient: GRADIENTS.success,
      onClick: () => {
        const params = new URLSearchParams();
        params.set('dateFilter', 'today');
        params.set('callStatus', 'received');
        navigate(`/leads?${params.toString()}`);
      },
      show: true,
    },
    {
      title: "Sales Today",
      value: `₹${(overviewData.salesToday || 0).toLocaleString('en-IN')}`,
      subtitle: "Total payments today",
      icon: <AttachMoney />,
      color: COLORS.indigo,
      gradient: GRADIENTS.indigo,
      onClick: () => {
        const params = new URLSearchParams();
        params.set('dateFilter', 'today');
        navigate(`/leads?${params.toString()}`);
      },
      show: true,
    },
    {
      title: "Deals Closed",
      value: overviewData.closedLeadsCount || 0,
      subtitle: "Closed Deals Today",
      icon: <Whatshot />,
      color: COLORS.purple,
      gradient: GRADIENTS.purple,
      onClick: () => {
        const params = new URLSearchParams();
        params.set('status', 'closed');
        params.set('dateFilter', 'today');
        navigate(`/leads?${params.toString()}`);
      },
      show: true,
    },
    {
      title: "Overdue Today",
      value: `₹${(overviewData.overdueAmountToday || 0).toLocaleString('en-IN')}`,
      subtitle: "Payment pending",
      icon: <Warning />,
      color: COLORS.error,
      gradient: GRADIENTS.error,
      onClick: () => {
        const params = new URLSearchParams();
        params.set('status', 'payment_overdue');
        params.set('dateFilter', 'today');
        navigate(`/leads?${params.toString()}`);
      },
      show: true,
    },
    {
      title: "Meetings",
      value: overviewData.meetings || 0,
      subtitle: "Scheduled today",
      icon: <CalendarToday />,
      color: COLORS.info,
      gradient: GRADIENTS.info,
      onClick: () => {
        const params = new URLSearchParams();
        params.set('dateFilter', 'today');
        params.set('status', 'meeting_scheduled');
        navigate(`/leads?${params.toString()}`);
      },
      show: true,
    },
  ];

  const displayDate = quotationFilters?.date ? new Date(quotationFilters.date) : new Date();
  const isToday = displayDate.toDateString() === new Date().toDateString();

  return (
    <Card sx={{
      p: 2,
      mb: 2,
      borderRadius: 2,
      background: mode === 'light'
        ? 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
        : 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          p: 1,
          borderRadius: 1.5,
          background: mode === 'light' ? alpha(COLORS.primary, 0.1) : alpha(COLORS.primary, 0.2),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${alpha(COLORS.primary, 0.2)}`
        }}>
          <DashboardIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
            {isToday ? "Today's Overview" : "Day Overview"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            {displayDate.toLocaleDateString('en-IN', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          '& > *': {
            flex: '1 1 140px',
            maxWidth: '160px',
            minWidth: '140px'
          }
        }}
      >
        {cards.map((card, index) => (
          <Card
            key={index}
            sx={{
              p: 1.5,
              height: '100px',
              borderRadius: 2,
              background: card.gradient,
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${alpha(card.color, 0.3)}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'rgba(255,255,255,0.3)',
              }
            }}
            onClick={card.onClick}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                p: 0.5,
                borderRadius: 1,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {React.cloneElement(card.icon, { sx: { fontSize: 16, color: 'white' } })}
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'white', fontSize: '0.7rem' }}>
                {card.title}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'white', mb: 0.25, fontSize: '1.1rem' }}>
                {card.value}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem', display: 'block', lineHeight: 1 }}>
                {card.subtitle}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>
    </Card>
  );
};

// ==================== QUOTATION FILTERS AREA - REUSABLE ====================
const QuotationFiltersArea = ({
  quotationFilters,
  handleFilterChange,
  mode,
  theme,
  alpha,
  months,
  years,
  currentYear,
}) => {
  const [isCustomYear, setIsCustomYear] = useState(
    quotationFilters.year && !years.includes(quotationFilters.year) && quotationFilters.year !== 'custom'
  );
  const [tempYear, setTempYear] = useState(quotationFilters.year || '');

  const handleYearSubmit = () => {
    if (tempYear && tempYear.length === 4 && !isNaN(tempYear)) {
      handleFilterChange('year', tempYear);
    } else if (!tempYear) {
      setIsCustomYear(false);
      handleFilterChange('year', currentYear.toString());
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <select
          value={quotationFilters.month}
          onChange={(e) => handleFilterChange('month', e.target.value)}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            background: mode === 'light' ? '#fff' : '#1E293B',
            color: theme.palette.text.primary,
            fontSize: '0.8rem',
            outline: 'none',
            cursor: 'pointer'
          }}
        >
          <option value="">All Months</option>
          {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>

        {isCustomYear || quotationFilters.year === 'custom' ? (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="YYYY"
              value={tempYear === 'custom' ? '' : tempYear}
              onChange={(e) => setTempYear(e.target.value)}
              onBlur={handleYearSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleYearSubmit();
              }}
              autoFocus
              style={{
                padding: '6px 10px',
                width: '60px',
                borderRadius: '6px',
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                background: mode === 'light' ? '#fff' : '#1E293B',
                color: theme.palette.text.primary,
                fontSize: '0.8rem',
                outline: 'none'
              }}
            />
            <IconButton
              size="small"
              onClick={() => {
                setIsCustomYear(false);
                handleFilterChange('year', currentYear.toString());
              }}
              sx={{ p: 0.5 }}
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ) : (
          <select
            value={quotationFilters.year}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setIsCustomYear(true);
                setTempYear('');
              } else {
                handleFilterChange('year', e.target.value);
              }
            }}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              background: mode === 'light' ? '#fff' : '#1E293B',
              color: theme.palette.text.primary,
              fontSize: '0.8rem',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
            <option value="custom">Custom...</option>
          </select>
        )}
      </Box>

      <input
        type="date"
        value={quotationFilters.date}
        onChange={(e) => handleFilterChange('date', e.target.value)}
        style={{
          padding: '6px 10px',
          borderRadius: '6px',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          background: mode === 'light' ? '#fff' : '#1E293B',
          color: theme.palette.text.primary,
          fontSize: '0.8rem',
          outline: 'none',
          cursor: 'pointer'
        }}
      />

      <Button
        size="small"
        variant="outlined"
        onClick={() => {
          handleFilterChange('reset', '');
        }}
        sx={{ fontSize: '0.75rem', textTransform: 'none', height: '32px' }}
      >
        Reset
      </Button>
    </Box>
  );
};

// ==================== QUOTATION METRICS COMPONENT ====================
const QuotationMetrics = ({
  quotationStats,
  role,
  GRADIENTS,
  theme,
  alpha,
  refreshingQuotations,
  quotationFilters,
  handleFilterChange,
  mode,
  months,
  years,
  currentYear,
}) => {
  if (!quotationStats || role !== 'admin') return null;

  const amountCards = [
    {
      title: "Total Quotation",
      amount: quotationStats.totalQuotationAmount || 0,
      icon: <AttachMoney />,
      color: "primary",
      description: "All quotations value",
      gradient: GRADIENTS.primary
    },
    {
      title: "Approved Amount",
      amount: quotationStats.totalApprovedAmount || 0,
      icon: <CheckCircle />,
      color: "success",
      description: "Approved quotations",
      gradient: GRADIENTS.success
    },
    {
      title: "Received Amount",
      amount: quotationStats.totalReceivedAmount || 0,
      icon: <Receipt />,
      color: "info",
      description: "Total received payments",
      gradient: GRADIENTS.info
    },
    {
      title: "Overdue Amount",
      amount: quotationStats.totalOverdueAmount || 0,
      icon: <Warning />,
      color: "error",
      description: "Overdue payments",
      gradient: GRADIENTS.error
    }
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            p: 1,
            borderRadius: 1.5,
            background: GRADIENTS.indigo,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AccountBalanceWallet sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
            Quotation Analytics
          </Typography>
          {/* {refreshingQuotations && <CircularProgress size={16} sx={{ ml: 1 }} />} */}
        </Box>

        <QuotationFiltersArea
          quotationFilters={quotationFilters}
          handleFilterChange={handleFilterChange}
          mode={mode}
          theme={theme}
          alpha={alpha}
          months={months}
          years={years}
          currentYear={currentYear}
        />
      </Box>

      <Box sx={{ position: 'relative' }}>
        {refreshingQuotations && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: alpha(theme.palette.background.paper, 0.4),
            backdropFilter: 'blur(2px)',
            borderRadius: 2
          }}>
            <CircularProgress size={30} thickness={4} color="primary" />
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          {amountCards.map((card, index) => (
            <CompactQuotationCard
              key={index}
              title={card.title}
              amount={card.amount}
              icon={card.icon}
              gradient={card.gradient}
              description={card.description}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};


// ==================== TEAM PERFORMANCE CARD - REUSABLE ====================
// ==================== TEAM PERFORMANCE CARD - UI ONLY CHANGED ====================
const TeamPerformanceCard = ({
  member,
  handleTeamMemberClick,
  teamQuotationData,
  role,
  COLORS,
  GRADIENTS,
  theme,
  alpha,
  quotationFilters,
  mode
}) => {
  const getPerformanceColor = (rate) => {
    if (rate >= 70) return COLORS.success;
    if (rate >= 40) return COLORS.warning;
    return COLORS.error;
  };

  const performanceColor = getPerformanceColor(member.conversionRate || 0);

  return (
    <Card
      onClick={() => handleTeamMemberClick(member)}
      sx={{
        p: 2,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        borderRadius: 2,
        backgroundColor: '#FFFFFF', // Force white background
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          borderColor: performanceColor,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: `linear-gradient(90deg, ${performanceColor}, ${alpha(performanceColor, 0.5)})`,
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Empty circle background */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={50}
            thickness={4}
            sx={{ 
              color: alpha(performanceColor, 0.2),
              position: 'absolute',
            }}
          />
          {/* Filled progress circle */}
          <CircularProgress
            variant="determinate"
            value={member.conversionRate || 0}
            size={50}
            thickness={4}
            sx={{ 
              color: performanceColor,
              position: 'relative',
            }}
          />
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: performanceColor, fontSize: '0.7rem' }}>
              {member.conversionRate || 0}%
            </Typography>
          </Box>
        </Box>
        
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1A1A1A', fontSize: '0.9rem' }}>
            {member.name}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666666', fontSize: '0.65rem' }}>
            Conversion Rate
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};


// ==================== SUPER ADMIN DASHBOARD - REUSABLE ====================
const SuperAdminDashboard = ({
  analytics,
  COLORS,
  GRADIENTS,
  mode,
  theme,
  alpha,
  navigate,
}) => {
  const stats = analytics?.platformStats || {};
  const companyPerformance = analytics?.companyPerformance || {};
  const companiesPerformance = analytics?.companiesPerformance || [];

  const superAdminStats = [
    {
      title: "Total Companies",
      value: stats.totalCompanies || 0,
      subtitle: "Registered companies",
      icon: <Business />,
      color: COLORS.primary,
      gradient: GRADIENTS.primary,
      onClick: () => navigate('/companies')
    },
    {
      title: "Total Admins",
      value: stats.totalAdmins || 0,
      subtitle: "Platform administrators",
      icon: <AdminPanelSettings />,
      color: COLORS.info,
      gradient: GRADIENTS.info,
    },
    {
      title: "Approved Companies",
      value: companyPerformance.approvedCompanies || 0,
      subtitle: "Active companies",
      icon: <CheckCircle />,
      color: COLORS.success,
      gradient: GRADIENTS.success,
    },
    {
      title: "Pending Approvals",
      value: companyPerformance.pendingApprovals || 0,
      subtitle: "Awaiting approval",
      icon: <Warning />,
      color: COLORS.warning,
      gradient: GRADIENTS.warning,
      onClick: () => navigate('/approvals')
    },
  ];

  return (
    <Box>
      {/* Top Stats Cards - COMPACT */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          '& > *': {
            flex: '1 1 180px',
            maxWidth: '220px',
            minWidth: '180px'
          }
        }}
      >
        {superAdminStats.map((stat, index) => (
          <StatCard
            key={index}
            title={stat.title}
            value={stat.value}
            subtitle={stat.subtitle}
            icon={stat.icon}
            color={stat.color}
            gradient={stat.gradient}
            onClick={stat.onClick}
          />
        ))}
      </Box>

      {/* Companies Performance Table - WITH ACTUAL DATA */}
      <Card sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        background: mode === 'light'
          ? 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)'
          : 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{
            p: 1,
            borderRadius: 1.5,
            background: GRADIENTS.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Business sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Companies Performance
          </Typography>
        </Box>

        {companiesPerformance.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Company Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Total Leads</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Closed Leads</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Performance</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Conversion Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companiesPerformance.slice(0, 10).map((company, index) => (
                  <TableRow key={company.companyId} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {company.companyName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {company.contactPerson}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                      {company.totalLeads}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: COLORS.success }}>
                      {company.closedLeads}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress
                          variant="determinate"
                          value={company.performanceScore}
                          size={30}
                          sx={{
                            color:
                              company.performanceScore >= 70 ? COLORS.success :
                                company.performanceScore >= 40 ? COLORS.warning : COLORS.error
                          }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {company.performanceScore}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                      {company.conversionRate}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No company performance data available.
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
};
const TeamMemberModal = ({ member, open, onClose, UID, CID, quotationFilters, mode, COLORS, GRADIENTS }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open && member) {
      fetchMemberDetails();
    }
  }, [open, member, quotationFilters]);

  const fetchMemberDetails = async () => {
    try {
      setLoading(true);
      const response = await getTeamMemberDetailedAnalytics(UID, CID, (member.memberId || member._id), quotationFilters);
      if (response.success) {
        setDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching member details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Team Member Performance</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : details ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Avatar sx={{ width: 60, height: 60, bgcolor: COLORS.primary }}>
                {details.memberInfo?.name?.charAt(0) || 'U'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{details.memberInfo?.name}</Typography>
                <Typography variant="body2" color="text.secondary">{details.memberInfo?.email}</Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Chip
                    label={`Conversion: ${details.leadMetrics?.conversionRate || 0}%`}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={`Performance: ${details.performanceScore || 0}/100`}
                    color="success"
                    size="small"
                  />
                </Box>
              </Box>
            </Box>

            {details.todaysOverview && (
              <Box sx={{ mb: 3 }}>
                <TodaysOverviewCard
                  overviewData={details.todaysOverview}
                  quotationFilters={quotationFilters}
                  mode={mode}
                  COLORS={COLORS}
                  GRADIENTS={GRADIENTS}
                  theme={theme}
                  alpha={alpha}
                  navigate={navigate}
                />
              </Box>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                <Tab label="Lead Analytics" />
                <Tab label="Quotation Analytics" />
                <Tab label="Performance Details" />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Lead Status Distribution</Typography>
                    {Object.entries(details.statusDistribution || {}).map(([status, count]) => (
                      <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{status}:</Typography>
                        <Typography variant="body2" fontWeight="bold">{count}</Typography>
                      </Box>
                    ))}
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Conversion Rate</Typography>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <CircularProgress
                        variant="determinate"
                        value={details.leadMetrics?.conversionRate || 0}
                        size={120}
                        thickness={4}
                        sx={{
                          color: details.leadMetrics?.conversionRate >= 30 ? COLORS.success :
                            details.leadMetrics?.conversionRate >= 15 ? COLORS.warning : COLORS.error,
                          mb: 2
                        }}
                      />
                      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>{details.leadMetrics?.conversionRate || 0}%</Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ p: 2, textAlign: 'center', background: GRADIENTS.primary, color: 'white' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{details.leadMetrics?.totalLeads || 0}</Typography>
                    <Typography variant="body2">Total Leads</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ p: 2, textAlign: 'center', background: GRADIENTS.success, color: 'white' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{details.leadMetrics?.closedLeads || 0}</Typography>
                    <Typography variant="body2">Closed Leads</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ p: 2, textAlign: 'center', background: GRADIENTS.info, color: 'white' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>{details.quotationMetrics?.totalQuotations || 0}</Typography>
                    <Typography variant="body2">Total Quotations</Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ p: 2, textAlign: 'center', background: GRADIENTS.purple, color: 'white' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>₹{(details.quotationMetrics?.totalReceivedAmount || 0).toLocaleString('en-IN')}</Typography>
                    <Typography variant="body2">Revenue Generated</Typography>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Quotation Performance</Typography>
                <Grid container spacing={2}>
                  {[
                    { title: "Shared", value: details.quotationMetrics?.quotationShared || 0, color: COLORS.info },
                    { title: "Approved", value: details.quotationMetrics?.quotationApproved || 0, color: COLORS.success },
                    { title: "Rejected", value: details.quotationMetrics?.quotationRejected || 0, color: COLORS.error },
                    { title: "Revised", value: details.quotationMetrics?.quotationRevised || 0, color: COLORS.warning },
                  ].map((stat, i) => (
                    <Grid item xs={6} sm={3} key={i}>
                      <Card sx={{ p: 2, textAlign: 'center', borderLeft: `4px solid ${stat.color}` }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: stat.color }}>{stat.value}</Typography>
                        <Typography variant="body2">{stat.title}</Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Financial Metrics</Typography>
              <Grid container spacing={2}>
                {[
                  { title: "Total Quotation", amount: details.quotationMetrics?.totalQuotationAmount || 0, gradient: GRADIENTS.primary },
                  { title: "Approved Amount", amount: details.quotationMetrics?.totalApprovedAmount || 0, gradient: GRADIENTS.success },
                  { title: "Received Amount", amount: details.quotationMetrics?.totalReceivedAmount || 0, gradient: GRADIENTS.info },
                  { title: "Overdue Amount", amount: details.quotationMetrics?.totalOverdueAmount || 0, gradient: GRADIENTS.error },
                ].map((metric, i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Card sx={{ p: 2, background: metric.gradient, color: 'white' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>₹{metric.amount.toLocaleString('en-IN')}</Typography>
                      <Typography variant="body2">{metric.title}</Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Performance Metrics</Typography>
                    {[
                      { label: "Hot Leads", value: details.leadMetrics?.hotLeads || 0 },
                      { label: "Warm Leads", value: details.leadMetrics?.warmLeads || 0 },
                      { label: "Cold Leads", value: details.leadMetrics?.coldLeads || 0 },
                      { label: "Follow-up Leads", value: details.leadMetrics?.followupLeads || 0 },
                      { label: "New Leads", value: details.leadMetrics?.newLeads || 0 },
                    ].map((m, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{m.label}:</Typography>
                        <Typography variant="body2" fontWeight="bold">{m.value}</Typography>
                      </Box>
                    ))}
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Overall Performance Score</Typography>
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                      <CircularProgress
                        variant="determinate"
                        value={details.performanceScore || 0}
                        size={120}
                        thickness={4}
                        sx={{
                          color: details.performanceScore >= 70 ? COLORS.success :
                            details.performanceScore >= 40 ? COLORS.warning : COLORS.error,
                          mb: 2
                        }}
                      />
                      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>{details.performanceScore || 0}</Typography>
                        <Typography variant="caption">/100</Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
          </Box>
        ) : (
          <Typography p={3}>No detailed information available.</Typography>
        )
        }
      </DialogContent >
    </Dialog >
  );
};

const DashboardAnalytics = ({ userData }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [teamQuotationData, setTeamQuotationData] = useState(null);
  const [teamMemberQuotationData, setTeamMemberQuotationData] = useState(null);

  // Filters for Quotation Analytics
  const [quotationFilters, setQuotationFilters] = useState({
    date: '',
    month: '',
    year: new Date().getFullYear().toString()
  });
  const [refreshingQuotations, setRefreshingQuotations] = useState(false);


  const UID = userData?._id;
  const role = userData?.role;
  const CID = userData?.companyAdded?.[0]?.companyId;

  const handleFilterChange = (field, value) => {
    if (field === 'reset') {
      setQuotationFilters({ date: '', month: '', year: new Date().getFullYear().toString() });
    } else {
      setQuotationFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  // Re-fetch analytics when filters change
  useEffect(() => {
    if (UID && role) {
      if (lastUpdated) {
        // If we already have data, it's a partial refresh
        fetchAnalytics(true);
        fetchTeamQuotationData();
        if (role === 'teamMember') {
          fetchTeamMemberPersonalQuotationData();
        }
      } else {
        // Initial load
        fetchAnalytics(false);
        fetchTeamQuotationData();
        if (role === 'teamMember') {
          fetchTeamMemberPersonalQuotationData();
        }
      }
    }
  }, [quotationFilters, UID, role, CID]);


  const COLORS = getColors(mode);
  const GRADIENTS = getGradients(mode);


  // ==================== RENDER HELPERS - MOVED OUTSIDE ====================
  // (Inner component definitions removed to prevent unmounting/remounting on state change)

  // Re-fetch analytics when filters change
  useEffect(() => {
    if (UID && role) {
      fetchAnalytics(lastUpdated !== null);
      if (role === 'admin') {
        fetchTeamQuotationData();
      } else if (role === 'teamMember') {
        fetchTeamMemberPersonalQuotationData();
      }
    }
  }, [quotationFilters, UID, role, CID]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  // ==================== MAIN FETCH FUNCTIONS ====================
  const fetchAnalytics = async (isPartial = false) => {
    try {
      if (!isPartial) setLoading(true);
      else setRefreshingQuotations(true);

      setError('');

      const response = await getDashboardAnalytics(UID, role, CID, quotationFilters);

      if (response.success) {
        setAnalytics(response.data);
        setLastUpdated(new Date());
        if (response.message) setMessage(response.message);
      } else {
        setError(response.message || 'Failed to load analytics');
      }
    } catch (err) {
      setError('Failed to load analytics');
      console.error('Analytics fetch error:', err);
    } finally {
      if (!isPartial) setLoading(false);
      setRefreshingQuotations(false);
    }
  };

  const fetchTeamQuotationData = async () => {
    try {
      if (role === 'admin' && CID) {
        const response = await getTeamPerformanceWithQuotations(UID, CID, quotationFilters);
        if (response.success) {
          setTeamQuotationData(response.data);
        } else {
          console.warn('No quotation data available for team');
          setTeamQuotationData(null);
        }
      }
    } catch (error) {
      console.error('Error fetching team quotation data:', error);
      setTeamQuotationData(null);
    }
  };

  const fetchTeamMemberPersonalQuotationData = async () => {
    try {
      const response = await getTeamMemberPersonalQuotationAnalytics(UID, CID, quotationFilters);
      if (response.success) {
        setTeamMemberQuotationData(response.data);
      }
    } catch (error) {
      console.error('Error fetching team member personal quotation data:', error);
    }
  };

  // In StatCard onClick handlers - FIXED VERSION
  const handleNavigateToLeads = (status = '', dateFilter = '') => {
    const params = new URLSearchParams();

    if (status) {
      // Map status to the actual filter values
      const statusMap = {
        'closed': 'closed',
        'hot': 'hot',
        'warm': 'warm',
        'dead': 'dead',
        'meeting': 'meeting_scheduled',
        'follow_up': 'follow-up',
        'rescheduled': 'rescheduled',
        'quotation': 'quotation_shared',
        'quotation_approved': 'quotation_approved',
        'quotation_rejected': 'rejected',
        'negotiation': 'negotiation',
        'payment_overdue': 'payment_overdue'
      };

      const actualStatus = statusMap[status] || status;
      params.set('status', actualStatus);
    }

    // For closed/hot/warm/dead leads, use ALL dates
    if (['closed', 'hot', 'warm', 'dead', 'payment_overdue', 'overdue'].includes(status)) {
      params.set('dateFilter', 'all');
    } else if (dateFilter) {
      params.set('dateFilter', dateFilter);
    } else {
      params.set('dateFilter', 'all');
    }

    console.log('Navigating to leads with params:', params.toString());

    // Use navigate and update URL
    navigate(`/leads?${params.toString()}`);

    // Force a URL update
    window.history.replaceState({}, '', `/leads?${params.toString()}`);
  };

  // FIXED: Team member click handler
  const handleTeamMemberClick = (member) => {
    console.log('Team member clicked:', member);
    setSelectedEmployee(member);
    setEmployeeModalOpen(true);
  };

  // FIXED: Modal close handler
  const handleModalClose = () => {
    setEmployeeModalOpen(false);
    setSelectedEmployee(null);
  };

  // Initial load is now handled by the consolidated useEffect above

  // ==================== RENDER LOGIC ====================
  if (loading)
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh', gap: 2 }}>
        <CircularProgress size={36} color="primary" />
        <Typography variant="body1" color="text.secondary">Loading analytics...</Typography>
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" action={
          <Button startIcon={<Refresh />} color="inherit" size="small" onClick={fetchAnalytics}>Retry</Button>
        }>
          {error}
        </Alert>
      </Box>
    );

  if (!analytics)
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          No analytics data available.
        </Alert>
      </Box>
    );

  const { overview, statusDistribution, personalPerformance, teamPerformance, todaysTasks, reminders, quotationStats, todaysLeads, todaysOverview, tomorrowsReminders, teamPerformanceSummary } = analytics;

  return (
    <Box >
      {message && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
          {message}
        </Alert>
      )}

      {/* Filters moved to Quotation Analytics header */}


      {/* Team Member Quotation Analytics - COMPACT */}
      {role === 'teamMember' && teamMemberQuotationData && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                p: 1,
                borderRadius: 1.5,
                background: GRADIENTS.indigo,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AccountBalanceWallet sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
                My Quotation Performance
              </Typography>
              {refreshingQuotations && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Box>

            {/* Reusable Filters Area for Employee */}
            <QuotationFiltersArea
              quotationFilters={quotationFilters}
              handleFilterChange={handleFilterChange}
              mode={mode}
              theme={theme}
              alpha={alpha}
              months={months}
              years={years}
              currentYear={currentYear}
            />
          </Box>

          {/* Team Member Quotation Stats - COMPACT */}
          <Box sx={{ position: 'relative' }}>
            {refreshingQuotations && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: alpha(theme.palette.background.paper, 0.4),
                backdropFilter: 'blur(2px)',
                borderRadius: 2
              }}>
                <CircularProgress size={30} thickness={4} color="primary" />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                pb: 1,
                '& > *': {
                  flex: '0 0 auto',
                  minWidth: '160px'
                }
              }}
            >
              {[
                {
                  title: "Total Quotation",
                  amount: teamMemberQuotationData?.totalQuotationAmount || 0,
                  icon: <AttachMoney />,
                  color: "primary",
                  description: "All quotations value",
                  gradient: GRADIENTS.primary
                },
                {
                  title: "Approved Amount",
                  amount: teamMemberQuotationData?.totalApprovedAmount || 0,
                  icon: <CheckCircle />,
                  color: "success",
                  description: "Approved quotations",
                  gradient: GRADIENTS.success
                },
                {
                  title: "Received Amount",
                  amount: teamMemberQuotationData?.totalReceivedAmount || 0,
                  icon: <Receipt />,
                  color: "info",
                  description: "Total received payments",
                  gradient: GRADIENTS.info
                },
                {
                  title: "Overdue Amount",
                  amount: teamMemberQuotationData?.totalOverdueAmount || 0,
                  icon: <Warning />,
                  color: "error",
                  description: "Overdue payments",
                  gradient: GRADIENTS.error
                }
              ].map((card, index) => (
                <CompactQuotationCard
                  key={index}
                  title={card.title}
                  amount={card.amount}
                  icon={card.icon}
                  gradient={card.gradient}
                  description={card.description}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ROLE-SPECIFIC DASHBOARDS */}
      {role === 'superAdmin' ? (
        <SuperAdminDashboard
          analytics={analytics}
          COLORS={COLORS}
          GRADIENTS={GRADIENTS}
          mode={mode}
          theme={theme}
          alpha={alpha}
          navigate={navigate}
        />
      ) : (
        <Box>
          {/* Quotation Metrics for Admin - PRIORITIZE synchronized data */}
          {role === 'admin' && (
            <QuotationMetrics
              quotationStats={teamQuotationData || quotationStats}
              role={role}
              GRADIENTS={GRADIENTS}
              theme={theme}
              alpha={alpha}
              refreshingQuotations={refreshingQuotations}
              quotationFilters={quotationFilters}
              handleFilterChange={handleFilterChange}
              mode={mode}
              months={months}
              years={years}
              currentYear={currentYear}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
              flexWrap: 'wrap',
              '& > *': {
                flex: '1 1 180px',
                maxWidth: '220px',
                minWidth: '180px'
              }
            }}
          >
            {/* Total Leads - Shows ALL leads with NO filters */}
            <StatCard
              title="Total Leads"
              value={overview.totalLeads}
              subtitle="All leads in system"
              icon={<People />}
              color={COLORS.primary}
              gradient={GRADIENTS.primary}
              onClick={() => {
                // Clear all filters - show ALL leads
                const params = new URLSearchParams();
                params.set('status', 'all');
                params.set('dateFilter', 'all');
                navigate(`/leads?${params.toString()}`);
              }}
            />


            <StatCard
              title="Closed Leads"
              value={overview.closedLeads}
              subtitle="Successfully closed (all time)"
              icon={<CheckCircle />}
              color={COLORS.success}
              gradient={GRADIENTS.success}
              onClick={() => handleNavigateToLeads('closed')} // This will set dateFilter='all'
            />


            <StatCard
              title="Hot Leads"
              value={statusDistribution.hot || 0}
              subtitle="High priority leads (all time)"
              icon={<Whatshot />}
              color={COLORS.error}
              gradient={GRADIENTS.error}
              onClick={() => handleNavigateToLeads('hot')} // This will set dateFilter='all'
            />

            <StatCard
              title="Conversion Rate"
              value={`${personalPerformance?.conversionRate || overview.conversionRate || 0}%`}
              subtitle="Lead conversion rate"
              icon={<TrendingUp />}
              color={COLORS.purple}
              gradient={GRADIENTS.purple}
            />
          </Box>

          {/* Today's Overview & Tomorrow's Reminders */}
          <TodaysOverviewCard
            overviewData={todaysOverview}
            quotationFilters={quotationFilters}
            mode={mode}
            COLORS={COLORS}
            GRADIENTS={GRADIENTS}
            theme={theme}
            alpha={alpha}
            navigate={navigate}
          />
          <TomorrowsRemindersCard
            remindersData={tomorrowsReminders}
            mode={mode}
            COLORS={COLORS}
            GRADIENTS={GRADIENTS}
            theme={theme}
            alpha={alpha}
            navigate={navigate}
          />

          {/* Team Performance for Admin */}
          {role === 'admin' && teamPerformanceSummary && teamPerformanceSummary.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 1.5,
                  background: GRADIENTS.purple,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Groups sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
                  Team Performance
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                {teamPerformanceSummary.map((member, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={member.memberId || index}>
                    <TeamPerformanceCard
                      member={member}
                      handleTeamMemberClick={handleTeamMemberClick}
                      teamQuotationData={teamQuotationData}
                      role={role}
                      COLORS={COLORS}
                      GRADIENTS={GRADIENTS}
                      theme={theme}
                      alpha={alpha}
                      quotationFilters={quotationFilters}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* Team Member Modal - FIXED GLITCHES AND DATA */}
      {selectedEmployee && (
        <TeamMemberModal
          member={selectedEmployee}
          open={employeeModalOpen}
          onClose={handleModalClose}
          UID={UID}
          CID={CID}
          quotationFilters={quotationFilters}
          mode={mode}
          COLORS={COLORS}
          GRADIENTS={GRADIENTS}
        />
      )}
    </Box>
  );
};

export default DashboardAnalytics;